import {
    Component, ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { SelectOption } from '@app/interfaces/sys/dict';
import { STColumn, STData, STPage, STRowClassName, STScroll, TablePosition } from '@app/interfaces/sys/st';
import { HighLightPipe } from '@app/pipe/high-light.pipe';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CommonService } from '@services/sys/common.service';
import { NzResizeEvent } from 'ng-zorro-antd/resizable';

@Component({
    selector: 'app-st',
    templateUrl: './st.component.html',
    styleUrls: ['./st.component.less'],
    providers: [HighLightPipe],
})
export class StComponent implements OnInit, OnChanges {
    @ViewChild('dataTable') dataTable!: ElementRef;
    @Input() data: STData[] = [];
    @Input() loading = false;
    @Input() ifIndex = false;
    @Input() hasEyes = false;
    @Input() hiddenHead = false;
    @Input() keyword = '';
    @Input() columns: STColumn[] = [];
    @Input() page!: STPage;
    @Input() scroll!: STScroll;
    @Input() size = 'small';
    @Input() ps = 10;
    @Input() pi = 1;
    @Input() pagination = false;
    @Input() total = 0;
    @Input() templates: {
        [key: string]: TemplateRef<void>;
    } = {};
    @Input() rowClassName!: STRowClassName;
    @Input() highlightId?: number;
    @Input() showTotal = false;
    @Input() nzShowSizeChanger = true;
    @Input() nzHideOnSinglePage = false;
    @Input() selectType = 'single';
    @Input() checkIds = [];
    @Input() canSelect = false;
    @Input() applyLoading = false;
    @Input() rowKey = 'id';
    @Input() allowDrop = false;
    @Input() sortSingle = true;
    @Input() resizeKey!: string;
    @Input() savePageSize = false;
    @Input() resizeMinWidth = this.commonService.stResizeMinWidth;
    @Output() changes = new EventEmitter();
    @Output() clickRow = new EventEmitter();
    @Output() changeDisplayedColumns = new EventEmitter();
    @Output() dropDown = new EventEmitter();
    isClick = false;
    align = 'center';

    sorts: string[] = [];
    sortMap: {
        [key: string]: string
    } = {
        descend: 'desc',
        ascend: 'asc',
    };

    @Output() selectChange = new EventEmitter();
    @Output() initData = new EventEmitter();
    setOfCheckedId = new Set<number>();
    checked = false;
    indeterminate = false;
    filterVisible = false;
    popoverVisible = false;
    allColumnsChecked = false;
    allColumnsIndeterminate = false;
    menuColumns: SelectOption[] = [];
    menuVisible = false;
    lastSortName!: string;
    tableWidth = 100;
    saveData: TablePosition = {} as TablePosition;
    tableColumnObj!: any;
    savePosition: any;
    autoColumns: STColumn[] = [];
    isGetTableHistory = false;
    width = 400;
    height = 200;
    savePositionLoading = false;
    savePositionTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private highlightPipe: HighLightPipe,
                private commonService: CommonService) {
    }

    ngOnInit(): void {
        if (this.checkIds.length > 0) {
            this.setOfCheckedId = new Set<number>(this.checkIds);
        }
    }

    get columnsStr(): string {
        return JSON.stringify(this.columns);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { checkIds, data, columns } = changes;
        if (checkIds && checkIds.currentValue.length > 0) {
            this.setOfCheckedId = new Set<number>(this.checkIds);
        }
        if (data && Array.from(this.setOfCheckedId).length > 0) {
            this.refreshCheckedStatus();
        }
        if (this.hasEyes && columns) {
            this.getMenuColumns();
        }
        if (!changes.data || JSON.stringify(changes.data.currentValue) === JSON.stringify(changes.data.previousValue)) {
            return;
        }
        this.refreshTableWidth();
    }

    handleSort(event: string, { index }: STColumn): void {
        const sortIndex = this.sorts.findIndex(item => item === index + ',desc' || item === index + ',asc');
        const sort = index + ',' + this.sortMap[event];
        if (!event) {
            this.sorts.splice(sortIndex, 1);
        } else if (event && sortIndex !== -1) {
            this.sorts[sortIndex] = sort;
        } else if (sortIndex === -1) {
            this.sortSingle ? this.sorts = [sort] : this.sorts.push(sort);
        }
        if (this.sortSingle) {
            this.columns.forEach(it => {
                it.sortOrder = it.index === index ? it.sortOrder : null;
            });
        }
        this.changes.emit({
            type: 'sort',
            sort: this.sorts,
        });
    }

    handleSearch(event: number): void {
        this.changes.emit({
            type: 'pi',
            pi: event,
        });
    }

    handleChangeSize(event: number): void {
        this.changes.emit({
            type: 'ps',
            ps: event,
        });
    }

    getClassName(data: STData, index: number): string {
        if (!this.rowClassName) {
            return '';
        }
        return this.rowClassName(data, index);
    }

    handleClickRow(data: STData): void {
        if (data.disabled) {
            return;
        }
        this.isClick = true;
        this.clickRow.emit(data);
        if (this.canSelect) {
            this.onItemChecked(data[this.rowKey], !this.setOfCheckedId.has(data[this.rowKey]));
        }
    }

    getValueByPath(data: STData, key: string, column: STColumn, i: number): any {
        let temp: any = data;
        if (key) {
            for (const p of key.split('.')) {
                temp = temp[p] || '';
            }
        }

        if (column?.format) {
            temp = column.format(data, column, i);
        }
        return this.formatHtml(temp);
    }

    formatHtml(value: string): string {
        return value?.toString().replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    handleFilterChange(data: number | number[], column: STColumn, index?: number): void {
        const key = column.filterIndex || column.index;
        this.changes.emit({
            type: 'filter',
            params: {
                [key as string]: data,
            },
        });
        if (!index) {
            return;
        }
        this.columns[index].filterVisible = false;
    }

    onResize({ width, height }: NzResizeEvent): void {
        this.width = width!;
        this.height = height!;
    }

    updateCheckedSet(id: number, checked: boolean, itemDisabled?: boolean): void {
        if (checked) {
            if (this.selectType === 'single') {
                this.setOfCheckedId.clear();
            }
            this.setOfCheckedId.add(id);
        } else {
            if (itemDisabled) {
                return;
            }
            this.setOfCheckedId.delete(id);
        }
    }

    onAllChecked(checked: boolean): void {
        this.data.forEach(item => this.updateCheckedSet(item[this.rowKey], checked, item.disabled));
        this.refreshCheckedStatus();
    }

    onItemChecked(id: number, checked: boolean): void {
        this.updateCheckedSet(id, checked);
        this.refreshCheckedStatus();
    }

    refreshCheckedStatus(): void {
        this.checked = this.data?.length > 0 && this.data.every(item => this.setOfCheckedId.has(item.id));
        this.indeterminate = !this.checked && this.data?.some(item => this.setOfCheckedId.has(item.id));
        this.selectChange.emit(Array.from(this.setOfCheckedId));
    }

    trackByData(item: STData): number {
        return item?.[this.rowKey];
    }

    resetCheckedIds(): void {
        this.checked = false;
        this.indeterminate = false;
        this.setOfCheckedId = new Set<number>();
        this.selectChange.emit(Array.from(this.setOfCheckedId));
    }

    updateColumnChecked(): void {
        this.allColumnsIndeterminate = false;
        if (this.allColumnsChecked) {
            this.menuColumns = this.menuColumns.map(item => ({
                ...item,
                checked: true,
            }));
        } else {
            this.menuColumns = this.menuColumns.map(item => ({
                ...item,
                checked: false,
            }));
        }
    }

    updateSingleColumnChecked(): void {
        if (this.menuColumns.every(item => !item.checked)) {
            this.allColumnsChecked = false;
            this.allColumnsIndeterminate = false;
        } else if (this.menuColumns.every(item => item.checked)) {
            this.allColumnsChecked = true;
            this.allColumnsIndeterminate = false;
        } else {
            this.allColumnsIndeterminate = true;
        }
    }

    getMenuColumns(): void {
        this.menuColumns = (this.columns || []).filter(item => !item.notShowInCheck).map(column => {
            return {
                value: column.title as string,
                label: column.title as string,
                checked: !column.iif || column.iif(column)
            };
        });
        this.updateSingleColumnChecked();
    }

    settingVisibleChange($event: boolean): void {
        if ($event) {
            this.getMenuColumns();
        }
    }

    apply(): void {
        const selectArr = this.menuColumns.filter(item => item.checked)?.map(column => column.value);
        this.changeDisplayedColumns.emit(selectArr);
    }

    changeMenuVisible(visible: boolean): void {
        this.menuVisible = visible;
        if (visible) {
            this.getMenuColumns();
        }
    }

    shouldRender(column: STColumn, data: STData, index: number): boolean {
        return this.getColSpan(column, data, index) !== 0;
    }

    formatColSpan(column: STColumn, data: STData, index: number): number | null {
        const span = this.getColSpan(column, data, index);
        return span > 1 ? span : null;
    }

    private getColSpan(column: STColumn, data: STData, index: number): number {
        const { colSpan } = column;
        if (typeof colSpan === 'function') {
            return colSpan(data, column, index) || 0;
        }
        if (typeof colSpan === 'number') {
            return colSpan;
        }
        return 1;
    }

    drop(event: CdkDragDrop<string[]>): void {
        this.dropDown.emit(event);
    }

    refreshTableWidth(): void {
        // this.getTableInitColumns();
        this.getTableWidth();
        this.initTablePosition();
    }

    // 初始化表格的宽度
    async initTablePosition(): Promise<void> {
        if (!this.isGetTableHistory) {
            this.resetTableHistory();
            await this.getTableHistory();
        }
        this.updateTableWidth();
    }

    getTableWidth(): void {
        setTimeout(() => {
            const tableElement = document.getElementById('stTable');
            this.tableWidth = ((tableElement?.clientWidth || 200) - (this.hasEyes ? 40 : 0) - (this.ifIndex ? 60 : 0));
        });
    }

    // getTableInitColumns(): void {
    //     const initColumns = this.columns || [];
    //     const hasAutoWidth = initColumns.some(it => it.isAutoWidth);
    //     this.getTableWidth();
    //     const columns = hasAutoWidth || this.resizeKey ? this.commonService.getContentWidth(initColumns, this.data, this.tableWidth) : initColumns;
    //     this.autoColumns = columns;
    // }

    resetTableHistory(): void {
        this.isGetTableHistory = false;
        this.tableColumnObj = {};
        this.savePosition = {};
        this.saveData = {} as TablePosition;
    }

    async getTableHistory(): Promise<void> {
        try {
            if (!this.resizeKey) {
                return;
            }
            this.loading = true;
            this.isGetTableHistory = true;
            const res = await this.commonService.getTablePosition(this.resizeKey);
            if (res && res.length > 0) {
                const saveData = res?.[0];
                this.saveData = saveData;
                const { size } = this.formatSavedData(saveData);
                if (this.savePageSize) {
                    this.ps = size || this.ps || 10;
                    this.initData.emit(this.ps);
                }
            } else {
                this.initData.emit();
            }
        } catch (e) {
            if (!this.savePageSize) {
                return;
            }
            this.ps = this.ps || 10;
            this.initData.emit(this.ps);
        } finally {
            this.loading = false;
        }
    }

    formatSavedData(tablePositions: TablePosition): {
        size: number,
        positions: any
    } {
        const { positions, formatterColumnsObj, size } = this.commonService.formatSavedData(tablePositions);
        this.savePosition = positions;
        this.tableColumnObj = formatterColumnsObj;
        return {
            size,
            positions,
        };
    }

    resizeStart(event: any, index: number): void {
        const sort = this.columns[index].sort;
        if (sort) {
            this.columns[index].hasSort = true;
            this.columns[index].sort = false;
        }
    }

    changeTableColumn(compareColumns: STColumn[]): void {
        this.columns.forEach((item, index) => {
            const currentItem = compareColumns[index];
            if (!currentItem) {
                return;
            }
            const { dragMinWidth, width, realWidth, titleWidth } = currentItem;
            const w = realWidth || width as string;
            item.realWidth = w;
            item.dragMinWidth = dragMinWidth;
            item.titleWidth = titleWidth;
        });
        console.log('columns', compareColumns);
    }

    // 重置表格宽度
    async resetTablePosition(): Promise<void> {
        if (this.savePositionLoading) {
            return ;
        }
        await this.saveTable({
            columns: [],
            size: this.savePosition?.size,
        });
        this.updateTableWidth();
    }

    updateTableWidth(): void {
        let arr: STColumn[] = this.columns;
        if (this.resizeKey) {
            console.log('updateTableWidth', this.data);
            arr = this.commonService.formatTableRealWidth(this.columns, this.tableWidth, this.tableColumnObj, this.data);
        }
        this.changeTableColumn(arr);
    }

    async saveTable(position: any): Promise<void> {
        try {
            if (!this.resizeKey) {
                return;
            }
            this.savePositionLoading = true;
            const params = {
                positions: JSON.stringify(position),
                filter: '{}',
                type: this.resizeKey,
            };
            const id = this.saveData?.id;
            const res = id ? await this.commonService.editTablePosition({
                id,
                ...params,
            }) : await this.commonService.addTablePosition(params);
            this.saveData = res;
            this.formatSavedData(res);
        } finally {
            this.savePositionLoading = false;
        }
    }

    resizeEnd(event: any, index: number): void {
        const resizedItem = this.columns[index];
        console.log('resizeEnd', event, index, this.columns, resizedItem);
        const dragBeforeWidth = parseFloat(resizedItem?.realWidth || resizedItem?.width as string);
        const { width } = event;
        const columns = this.commonService.resizeColumn({
            columns: this.columns,
            resizeIndex: index,
            resizedWidth: width,
            tableWidth: this.tableWidth
        });
        if (parseFloat(width) !== dragBeforeWidth) {
            this.changeTableColumn(columns);
            this.saveColumns();
        }
        if (resizedItem?.hasSort) {
            this.columns[index].sort = true;
        }
    }

    saveColumns(): void {
        if (this.savePositionTimer) {
            clearTimeout(this.savePositionTimer);
            this.savePositionTimer = null;
        }
        this.savePositionTimer = setTimeout(() => {
            this.saveTable({
                columns: this.columns,
                size: this.savePosition?.size,
            });
        }, 300);
    }
}
